import React, { useEffect } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Clock } from "lucide-react";
import CalcHowItWorksModal from "@/components/CalcHowItWorksModal";
import usePageSEO from "@/hooks/usePageSEO";

const DEFAULT_METHODOLOGY = {
  methodology:
    "This tool uses the inputs you provide plus industry-standard conventions to produce an educational estimate. All calculations are deterministic for the inputs given — there is no simulation of market variability or sequence-of-returns risk.",
  assumptions: [
    "Inputs are taken at face value; no inflation adjustment is applied unless explicitly noted on the input field.",
    "Where carrier-specific rates are relevant (e.g., annuity payout rates, IUL caps), illustrative figures are used, not quotes.",
    "Taxes, fees, and future contribution changes are not modeled.",
  ],
  limitations: [
    "This is an educational estimate, not a projection, guarantee, or offer of any insurance or annuity product.",
    "Actual product outcomes depend on the carrier, state availability, underwriting, and current contract terms.",
    "Consult a licensed professional before acting on any result.",
  ],
};

/**
 * Page wrapper for every calculator. Renders Nav + Footer + a consistent
 * editorial header, and (Phase 11.5) a transparency "How this works" modal.
 */
export const CalculatorShell = ({
  eyebrow = "Interactive Tool",
  title,
  blurb,
  duration,
  children,
  Icon,
  howItWorks = {},
}) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  usePageSEO({
    title: `${title} — Interactive Calculator`,
    description: blurb,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: title,
      description: blurb || "Educational financial calculator.",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@id": "https://lifespanwealthpartners.com/#org" },
    },
  });

  const h = { ...DEFAULT_METHODOLOGY, ...(howItWorks || {}) };

  return (
    <div data-testid="calculator-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
            <span className="lw-eyebrow">{eyebrow}</span>
            <div className="flex items-center gap-4">
              {duration && (
                <span className="lw-pill" data-testid="tool-duration">
                  <Clock size={12} /> {duration}
                </span>
              )}
              <CalcHowItWorksModal
                calcName={title}
                methodology={h.methodology}
                assumptions={h.assumptions}
                limitations={h.limitations}
              />
            </div>
          </div>
          <div className="flex items-start gap-4 mb-6">
            {Icon && (
              <div
                className="hidden sm:flex w-12 h-12 rounded-xl items-center justify-center bg-lw-navy text-[#FAF7F2]"
                aria-hidden="true"
              >
                <Icon size={22} />
              </div>
            )}
            <div>
              <h1
                data-testid="tool-title"
                className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]"
              >
                {title}
              </h1>
              {blurb && (
                <p className="mt-3 max-w-2xl text-base sm:text-lg text-lw-muted leading-relaxed">
                  {blurb}
                </p>
              )}
            </div>
          </div>
          <div className="gold-hairline opacity-60 my-8" />
          <div className="space-y-8">{children}</div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CalculatorShell;
